import {
  AfterViewInit,
  ComponentFactory,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { JdToolTipComponent } from './tool-tip.component';
import { isNotNil } from '../core/convert';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[jd-tool-tip], [jd-tool-tip][jd-title]'
})
export class JdToolTipDirective implements AfterViewInit, OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  // [NOTE] Here hard coded, and jdTitle used only under NzTooltipDirective currently.
  isTooltipOpen = false;
  isDynamicTooltip = false; // Indicate whether current tooltip is dynamic created
  delayTimer; // Timer for delay enter/leave
  _title: string | TemplateRef<void>;
  _content: string | TemplateRef<void>;
  _overlayClassName: string;
  _overlayStyle: { [key: string]: string };
  _mouseEnterDelay: number;
  _mouseLeaveDelay: number;
  _visible: boolean;
  _trigger: string;
  _placement: string;
  factory: ComponentFactory<
    JdToolTipComponent
  > = this.resolver.resolveComponentFactory(JdToolTipComponent);
  @Output() jdVisibleChange = new EventEmitter<boolean>();

  @Input('jd-tooltip')
  set jdTitle(title: string | TemplateRef<void>) {
    this._title = title;
    this.updateCompValue('jdTitle', title);
  }

  get jdTitle(): string | TemplateRef<void> {
    return this._title;
  }

  @Input('jdTitle')
  set setTitle(title: string | TemplateRef<void>) {
    this.jdTitle = title;
  }

  @Input()
  set jdContent(value: string | TemplateRef<void>) {
    this._content = value;
    this.updateCompValue('jdContent', value);
  }

  get jdContent(): string | TemplateRef<void> {
    return this._content;
  }

  @Input()
  set jdOverlayClassName(value: string) {
    this._overlayClassName = value;
    this.updateCompValue('jdOverlayClassName', value);
  }

  get jdOverlayClassName(): string {
    return this._overlayClassName;
  }

  @Input()
  set jdOverlayStyle(value: { [key: string]: string }) {
    this._overlayStyle = value;
    this.updateCompValue('jdOverlayStyle', value);
  }

  get jdOverlayStyle(): { [key: string]: string } {
    return this._overlayStyle;
  }

  @Input()
  set jdMouseEnterDelay(value: number) {
    this._mouseEnterDelay = value;
    this.updateCompValue('jdMouseEnterDelay', value);
  }

  get jdMouseEnterDelay(): number {
    return this._mouseEnterDelay;
  }

  @Input()
  set jdMouseLeaveDelay(value: number) {
    this._mouseLeaveDelay = value;
    this.updateCompValue('jdMouseLeaveDelay', value);
  }

  get jdMouseLeaveDelay(): number {
    return this._mouseEnterDelay;
  }

  @Input()
  set jdVisible(value: boolean) {
    this._visible = value;
    this.updateCompValue('jdVisible', value);
  }

  get jdVisible(): boolean {
    return this._visible;
  }

  @Input()
  set jdTrigger(value: string) {
    this._trigger = value;
    this.updateCompValue('jdTrigger', value);
  }

  get jdTrigger(): string {
    return this._trigger;
  }

  @Input()
  set jdPlacement(value: string) {
    this._placement = value;
    this.updateCompValue('jdPlacement', value);
  }

  get jdPlacement(): string {
    return this._placement;
  }

  @HostBinding('class.ant-tooltip-open')
  get isOpen(): boolean {
    return this.isTooltipOpen;
  }

  private show(): void {
    this.tooltip.show();
    this.isTooltipOpen = true;
  }

  private hide(): void {
    this.tooltip.hide();
    this.isTooltipOpen = false;
  }

  private delayEnterLeave(
    isOrigin: boolean,
    isEnter: boolean,
    delay: number = -1
  ): void {
    if (this.delayTimer) {
      window.clearTimeout(this.delayTimer);
      this.delayTimer = null;
    } else if (delay > 0) {
      this.delayTimer = window.setTimeout(() => {
        this.delayTimer = null;
        isEnter ? this.show() : this.hide();
      }, delay * 1000);
    } else {
      isEnter && isOrigin ? this.show() : this.hide();
    }
  }

  // tslint:disable-next-line:no-any
  updateCompValue(key: string, value: any): void {
    if (this.isDynamicTooltip && isNotNil(value)) {
      this.tooltip[key] = value;
    }
  }

  constructor(
    public elementRef: ElementRef,
    public hostView: ViewContainerRef,
    public resolver: ComponentFactoryResolver,
    public renderer: Renderer2,
    @Optional() public tooltip: JdToolTipComponent
  ) {}

  ngOnInit(): void {
    // Support faster tooltip mode: <a jd-tooltip="xxx"></a>. [NOTE] Used only under NzTooltipDirective currently.
    if (!this.tooltip) {
      const tooltipComponent = this.hostView.createComponent(this.factory);
      this.tooltip = tooltipComponent.instance;
      // Remove element when use directive https://github.com/NG-ZORRO/ng-zorro-antd/issues/1967
      this.renderer.removeChild(
        this.renderer.parentNode(this.elementRef.nativeElement),
        tooltipComponent.location.nativeElement
      );
      this.isDynamicTooltip = true;
      const properties = [
        'jdTitle',
        'jdContent',
        'jdOverlayClassName',
        'jdOverlayStyle',
        'jdMouseEnterDelay',
        'jdMouseLeaveDelay',
        'jdVisible',
        'jdTrigger',
        'jdPlacement'
      ];
      properties.forEach(property =>
        this.updateCompValue(property, this[property])
      );
      this.tooltip.jdVisibleChange
        .pipe(
          takeUntil(this.unsubscribe$),
          distinctUntilChanged()
        )
        .subscribe(data => {
          this._visible = data;
          this.jdVisibleChange.emit(data);
        });
    }
    this.tooltip.setOverlayOrigin(this);
  }

  ngAfterViewInit(): void {
    if (this.tooltip.jdTrigger === 'hover') {
      let overlayElement;
      this.renderer.listen(this.elementRef.nativeElement, 'mouseenter', () =>
        this.delayEnterLeave(true, true, this.tooltip.jdMouseEnterDelay)
      );
      this.renderer.listen(this.elementRef.nativeElement, 'mouseleave', () => {
        this.delayEnterLeave(true, false, this.tooltip.jdMouseLeaveDelay);
        if (this.tooltip.overlay.overlayRef && !overlayElement) {
          // NOTE: we bind events under "mouseleave" due to the overlayRef is only created after the overlay was completely shown up
          overlayElement = this.tooltip.overlay.overlayRef.overlayElement;
          this.renderer.listen(overlayElement, 'mouseenter', () =>
            this.delayEnterLeave(false, true)
          );
          this.renderer.listen(overlayElement, 'mouseleave', () =>
            this.delayEnterLeave(false, false)
          );
        }
      });
    } else if (this.tooltip.jdTrigger === 'focus') {
      this.renderer.listen(this.elementRef.nativeElement, 'focus', () =>
        this.show()
      );
      this.renderer.listen(this.elementRef.nativeElement, 'blur', () =>
        this.hide()
      );
    } else if (this.tooltip.jdTrigger === 'click') {
      this.renderer.listen(this.elementRef.nativeElement, 'click', e => {
        e.preventDefault();
        this.show();
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
